import useHasApp from 'hooks/useHasApp';
import {
  heroDefaultLanding,
  journeysDefaultLanding,
  presentationsDefaultLanding,
  ungatedDefaultLanding,
} from 'routes/routes';

export type DefaultLanding = Readonly<{
  /** Where this user belongs when nothing more specific is known. */
  route: string;
  /** True until entitlement has resolved. `route` already holds the safe default meanwhile. */
  isPending: boolean;
}>;

/**
 * The apps that own a landing page, in the order a user holding several should see them
 * (ENG-1169): Journeys, then Presentations, then the Canvassing App. `clientId` is the app's id
 * in `company.apps`, and each route is gated on that same id in App.tsx.
 */
const LANDING_PRECEDENCE: readonly Readonly<{ clientId: string; route: string }>[] = [
  { clientId: 'polaris', route: journeysDefaultLanding },
  { clientId: 'presentations', route: presentationsDefaultLanding },
  { clientId: 'hero', route: heroDefaultLanding },
];

/**
 * Single source of truth for "where does this user belong when we have no better idea" (ENG-1169).
 *
 * A user lands in the highest-precedence app they actually hold, so a company that bought one
 * product opens on that product rather than on a page it has nothing to do with. Order is a
 * product call, not a technical one: Journeys is where most companies spend their day,
 * Presentations is the standalone product, and the Canvassing App's dashboard is the fallback for
 * a company that only canvasses.
 *
 * Holding nothing -- and every moment before entitlement resolves -- answers Leads, the one route
 * with no `RequireApp` in front of it. A landing that can bounce is a landing that can loop, so
 * the unknown case must never name a gated page.
 *
 * `DefaultLandingRedirect` is the sole consumer: it resolves "/", which is where every page that
 * cannot pick a destination itself sends people -- login, both error pages, the 404's "Go Home",
 * the escape button on AppNotAvailable, and a company switch. Routing them all through "/" rather
 * than having each resolve its own target is what keeps a wrong answer from being possible in one
 * place and right in another.
 */
export default function useDefaultLandingRoute(): DefaultLanding {
  const hasApp = useHasApp();

  // `hasApp` is a plain factory, not a hook, so asking it about each app in turn is safe.
  const access = LANDING_PRECEDENCE.map((app) => ({ ...app, ...hasApp(app.clientId) }));

  if (access.some((app) => app.isPending)) {
    return { route: ungatedDefaultLanding, isPending: true };
  }

  return {
    route: access.find((app) => app.hasApp)?.route ?? ungatedDefaultLanding,
    isPending: false,
  };
}
